import React, { useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff } from "lucide-react";
import Button from "../common/Button";
import Modal from "../common/Modal";
import Input from "../common/Input";
import Switch from "../common/Switch";
import { inviteSubAccount } from "../../services/subAccountService";
import { color } from "../../styles/theme";

// Promoter Panel spec ch.11.2 - the 9 permission blocks. Keys match
// SubAccountPermissionDto's PascalCase fields on the backend exactly.
const PERMISSION_KEYS = [
  "canManageEvents", "canManageRooms", "canManageAccessQrCheckIn", "canManageLocationExceptions", "canManageGallery",
  "canManageSingleMedia", "canManagePricing", "canManageWallet", "canManageSupport", "canManageSubAccounts",
];
const toApiKey = (key) => key.charAt(0).toUpperCase() + key.slice(1);

const InviteSubAccountModal = ({ onClose, onInvited }) => {
  const { t } = useTranslation(["subAccounts", "common"]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [permissions, setPermissions] = useState(() =>
    PERMISSION_KEYS.reduce((acc, k) => ({ ...acc, [k]: false }), {})
  );
  const [confirmed, setConfirmed] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!confirmed) {
      toast.error(t("inviteModal.confirmRulesRequired"));
      return;
    }
    if (!password) {
      toast.error(t("inviteModal.passwordRequired"));
      return;
    }
    setSending(true);
    try {
      const payload = {
        Name: name.trim(),
        Email: email.trim(),
        Permissions: PERMISSION_KEYS.reduce((acc, k) => ({ ...acc, [toApiKey(k)]: permissions[k] }), {}),
        Password: password,
      };
      const res = await inviteSubAccount(payload);
      toast.success(res.data?.Message || t("inviteModal.success"));
      onInvited();
    } catch (err) {
      toast.error(err.response?.data?.Message || t("inviteModal.error"));
      setPassword("");
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      title={t("inviteModal.title")}
      subtitle={t("inviteModal.subtitle")}
      onClose={onClose}
      busy={sending}
      maxWidth={440}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={sending}>{t("common:actions.cancel")}</Button>
          <Button onClick={handleSubmit} disabled={sending || !name.trim() || !email.trim() || !confirmed || !password}>
            {sending ? t("inviteModal.sending") : t("inviteModal.send")}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        <Input label={t("inviteModal.nameLabel")} value={name} onChange={(e) => setName(e.target.value)} placeholder={t("inviteModal.namePlaceholder")} required />
        <Input label={t("inviteModal.emailLabel")} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("inviteModal.emailPlaceholder")} required />

        <p style={s.sectionLabel}>{t("inviteModal.permissionsHeading")}</p>
        <div style={s.list}>
          {PERMISSION_KEYS.map((key, i) => (
            <div key={key} style={{ ...s.row, ...(i === PERMISSION_KEYS.length - 1 ? { borderBottom: "none" } : {}) }}>
              <span style={s.rowLabel}>{t(`permissions.${key}`)}</span>
              <Switch
                checked={permissions[key]}
                onChange={() => setPermissions((prev) => ({ ...prev, [key]: !prev[key] }))}
                disabled={sending}
              />
            </div>
          ))}
        </div>

        <p style={s.sectionLabel}>{t("inviteModal.confirmIdentityHeading")}</p>
        <div style={{ position: "relative" }}>
          <Input
            label={t("inviteModal.passwordLabel")}
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t("inviteModal.passwordPlaceholder")}
            autoComplete="current-password"
            required
          />
          <button type="button" onClick={() => setShowPassword((v) => !v)} style={s.eyeBtn} tabIndex={-1} aria-label={t("inviteModal.togglePasswordVisibility")}>
            {showPassword ? <EyeOff size={16} strokeWidth={2} /> : <Eye size={16} strokeWidth={2} />}
          </button>
        </div>
        <p style={s.passwordHint}>{t("inviteModal.passwordHint")}</p>

        <label style={s.checkboxRow}>
          <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} disabled={sending} />
          <span>{t("inviteModal.confirmRules")}</span>
        </label>
      </form>
    </Modal>
  );
};

const s = {
  sectionLabel: { margin: "18px 0 2px", fontSize: 12, fontWeight: 700, color: color.gray600, textTransform: "uppercase", letterSpacing: 0.3 },
  list: { display: "flex", flexDirection: "column" },
  row: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "11px 2px", borderBottom: `1px solid ${color.gray100}` },
  rowLabel: { fontSize: 13.5, color: color.gray800, fontWeight: 500 },
  checkboxRow: { display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, color: color.gray700, margin: "16px 0 2px", cursor: "pointer", lineHeight: 1.4 },
  eyeBtn: {
    position: "absolute", right: 10, top: 33, background: "none", border: "none", cursor: "pointer",
    padding: "4px 6px", lineHeight: 1, color: color.gray500, display: "flex",
  },
  passwordHint: { fontSize: 11.5, color: color.textMuted, margin: "-10px 0 0", lineHeight: 1.5 },
};

export default InviteSubAccountModal;
